(ns app.front.view.common.label-file-transfer
  (:require [ajax.core :refer [PUT]]
            [app.domain.label.model :as label-model]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.process :as process]
            [app.front.ui.modal :as ui-modal]
            [app.front.util :refer [for-frag]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def inspection-context
  "Context for inspection (authority-group) label import/export. Groups and labels are created with
   commands, one request each."
  {:create-group-command :inspection/create-label-group
   :create-label-command :inspection/create-label
   :download-command     :inspection/download-labels
   :delete-group-command :inspection/delete-label-group
   :parent-id-key        :authority-group-id})

(def workspace-context
  "Context for workspace label import/export. Labels on a workspace are managed through its state
   machine, so each parsed group is created with a single signal carrying its labels. The caller adds
   :parent-id-value (the workspace id) and :signal-target-id (the phase the signal is applied to)."
  {:create-group-signal :process.signal/create-workspace-label-group
   :download-command    :label/download-workspace-labels
   :parent-id-key       :workspace-id})

(defn build-export-group-params
  "Build query string for label-group-ids as repeated params.
   Returns nil if group-ids is empty, otherwise returns '&label-group-ids=1&label-group-ids=2'."
  [group-ids]
  (when (seq group-ids)
    (str "&"
         (str/join "&"
                   (map (fn [group-id]
                          (str "label-group-ids="
                               (js/encodeURIComponent (str group-id))))
                        (sort group-ids))))))

(rf/reg-sub ::export-modal-open?
  (fn [db _]
    (get db ::export-modal-open? false)))

(rf/reg-sub ::selected-group-ids-to-export
  (fn [db _]
    (get db ::selected-group-ids-to-export #{})))

(rf/reg-sub ::import-in-progress?
  (fn [db _]
    (get db ::import-in-progress? false)))

(rf/reg-sub ::import-conflict-state
  (fn [db _]
    (get db ::import-conflict-state)))

(rf/reg-event-db ::set-import-upload-state
  (fn [db [_ component-id file-hash]]
    (assoc db ::import-upload-state {:component-id component-id
                                     :file-hash    file-hash})))

(rf/reg-event-fx ::clear-import-upload-state
  (fn [{:keys [db]} _]
    (if-let [{:keys [component-id file-hash]} (::import-upload-state db)]
      {:db         (dissoc db ::import-upload-state)
       :dispatch-n [[::upload/remove-pending-file component-id :placeholder]
                    [::upload/remove-pending-file component-id file-hash]]}
      {})))

(rf/reg-sub ::export-in-progress?
  (fn [db _]
    (get db ::export-in-progress? false)))

(rf/reg-event-db ::open-export-modal
  (fn [db _]
    (assoc db ::export-modal-open? true)))

(rf/reg-event-db ::close-export-modal
  (fn [db _]
    (-> db
        (dissoc ::export-modal-open?)
        (dissoc ::selected-group-ids-to-export))))

(rf/reg-event-db ::toggle-export-group-selection
  (fn [db [_ group-id]]
    (update db ::selected-group-ids-to-export util/toggle-in-set group-id)))

(rf/reg-event-db ::select-all-export-groups
  (fn [db [_ group-ids]]
    (assoc db ::selected-group-ids-to-export (set group-ids))))

(rf/reg-event-db ::deselect-all-export-groups
  (fn [db _]
    (assoc db ::selected-group-ids-to-export #{})))

(defn- content-disposition-filename [content-disposition]
  (some->> content-disposition
           (re-find #"filename=\"?([^\";]+)\"?")
           second))

(defn- trigger-blob-download!
  "Create a temporary download link, click it, and clean up."
  [blob filename]
  (let [download-url (.createObjectURL js/URL blob)
        link         (.createElement js/document "a")]
    (set! (.-href link) download-url)
    (set! (.-download link) filename)
    (.appendChild (.-body js/document) link)
    (.click link)
    (.remove link)
    (.revokeObjectURL js/URL download-url)))

(rf/reg-fx ::fetch-blob-download
  (fn [{:keys [href default-filename on-success on-failure]}]
    (-> (js/fetch href #js {:credentials "same-origin"})
        (.then (fn [response]
                 (if (.-ok response)
                   (-> (.blob response)
                       (.then (fn [blob]
                                (let [content-disposition (some-> response .-headers (.get "Content-Disposition"))
                                      filename            (or (content-disposition-filename content-disposition)
                                                              default-filename)]
                                  (trigger-blob-download! blob filename)
                                  (>evt on-success))))
                       (.catch (fn [_] (>evt on-failure))))
                   (>evt on-failure))))
        (.catch (fn [_] (>evt on-failure))))))

(rf/reg-event-fx ::export-labels.failed
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::export-in-progress?)
     :dispatch [:notifications/add :toast :error :error/error :error/unknown]}))

(rf/reg-event-fx ::export-labels.success
  (fn [{:keys [db]} _]
    {:db       (-> db
                   (dissoc ::export-in-progress?)
                   (dissoc ::export-modal-open?)
                   (dissoc ::selected-group-ids-to-export))
     :dispatch [:notifications/add :toast :success :success :labels/groups-exported]}))

(rf/reg-event-fx ::export-labels
  (fn [{:keys [db]} [_ context selected-ids]]
    (let [base-href (file-download/download-href
                      (:download-command context)
                      {(:parent-id-key context) (:parent-id-value context)})
          href      (str base-href (build-export-group-params selected-ids))]
      {:db                   (assoc db ::export-in-progress? true)
       ::fetch-blob-download {:href             href
                              :default-filename "labels.zip"
                              :on-success       [::export-labels.success]
                              :on-failure       [::export-labels.failed]}})))

(defn- normalize-group-name [name]
  (some-> name str/trim str/lower-case))

(rf/reg-event-fx ::close-import-conflict-modal
  (fn [{:keys [db]} _]
    {:db       (-> db
                   (dissoc ::import-conflict-state)
                   (dissoc ::import-in-progress?)
                   (dissoc ::existing-group-names))
     :dispatch [::clear-import-upload-state]}))

(rf/reg-event-db ::update-conflict-new-name
  (fn [db [_ new-name]]
    (assoc-in db [::import-conflict-state :new-name] new-name)))

(rf/reg-event-fx ::import-with-renamed-group
  (fn [{:keys [db]} [_ context]]
    (let [{:keys [group-data new-name remaining-files]} (::import-conflict-state db)
          existing-names                                (::existing-group-names db)
          trimmed-name                                  (str/trim (or new-name ""))]
      {:db       (-> db
                     (dissoc ::import-conflict-state)
                     (assoc ::existing-group-names (assoc existing-names trimmed-name nil)))
       :dispatch [::create-imported-group
                  context
                  (assoc group-data :name trimmed-name)
                  remaining-files]})))

(rf/reg-event-fx ::skip-conflicting-import
  (fn [{:keys [db]} [_ context]]
    (let [{:keys [remaining-files]} (::import-conflict-state db)
          existing-names            (::existing-group-names db)]
      {:db       (dissoc db ::import-conflict-state)
       :dispatch [::process-next-import-file context remaining-files existing-names]})))

(rf/reg-event-fx ::process-import-files
  (fn [{:keys [db]} [_ context parsed-files existing-names]]
    (if (and (:create-group-signal context) (not (:signal-target-id context)))
      ;; Misconfigured caller: signal import requires a phase target.
      (do (js/console.error "::process-import-files - workspace-context is missing :signal-target-id" (clj->js context))
          {:dispatch-n [[::clear-import-upload-state]
                        [:notifications/add :toast :error :error/error :labels/import-failed]]})
      {:db       (-> db
                     (assoc ::import-in-progress? true)
                     (assoc ::existing-group-names existing-names))
       :dispatch [::process-next-import-file context parsed-files existing-names]})))

(rf/reg-event-fx ::process-next-import-file
  (fn [{:keys [db]} [_ context files existing-names]]
    (if (empty? files)
      {:db         (-> db
                       (dissoc ::import-in-progress?)
                       (dissoc ::existing-group-names))
       :dispatch-n [[::clear-import-upload-state]
                    [:notifications/add :toast :success :success :labels/import-complete]]}
      (let [[{:keys [name labels]} & remaining] files
            candidate                           (normalize-group-name name)
            conflict?                           (some #(= candidate (normalize-group-name %)) (keys existing-names))]
        (if conflict?
          {:db (assoc db ::import-conflict-state
                      {:group-data      {:name name :labels labels}
                       :original-name   name
                       :new-name        name
                       :remaining-files (vec remaining)
                       :existing-names  existing-names
                       :context         context})}
          {:db       (assoc db ::existing-group-names (assoc existing-names name nil))
           :dispatch [::create-imported-group context {:name name :labels labels} (vec remaining)]})))))

(rf/reg-event-fx ::create-imported-group
  (fn [{:keys [db]} [_ context {:keys [name labels]} remaining-files]]
    (let [existing-names (::existing-group-names db)
          signal         (:create-group-signal context)
          target-id      (:signal-target-id context)]
      (cond
        ;; One signal per group: the group and its labels are created in a single transaction.
        (and signal target-id)
        {:dispatch [::process/signal
                    target-id
                    [signal {:name   name
                             :labels (vec labels)}]
                    {:on-success [::process-next-import-file context remaining-files existing-names]}]}

        signal
        ;; Should have been caught in ::process-import-files; guard against direct calls.
        (do (js/console.error "::create-imported-group - a signal context needs :signal-target-id"
                              (clj->js context))
            {:dispatch [::import-failed]})

        :else
        {:dispatch [:comm/command-monitored
                    {:command    (:create-group-command context)
                     :method     :post
                     :data       {(:parent-id-key context) (:parent-id-value context)
                                  :name                    name}
                     :on-success [::imported-group-created context labels remaining-files existing-names]
                     :on-done    [::import-failed]
                     :on-error   [::import-failed]}]}))))

(rf/reg-event-fx ::imported-group-created
  (fn [_ [_ context labels remaining-files existing-names status response]]
    (let [created-group-id (ffirst response)]
      (if created-group-id
        {:dispatch-n [[::entities/apply-changes status response]
                      [::create-imported-labels created-group-id labels 0 context remaining-files existing-names]]}
        {:dispatch [::import-failed]}))))

(rf/reg-event-fx ::create-imported-labels
  (fn [_ [_ label-group-id labels label-idx context remaining-files existing-names]]
    (if (>= label-idx (count labels))
      {:dispatch [::process-next-import-file context remaining-files existing-names]}
      (let [label-name (nth labels label-idx)]
        {:dispatch [:comm/command-monitored
                    {:command    (:create-label-command context)
                     :method     :post
                     :data       {:label-group-id label-group-id
                                  :name           label-name}
                     :on-success [::imported-label-created label-group-id labels (inc label-idx) context remaining-files existing-names]
                     :on-done    [::import-failed]
                     :on-error   [::import-failed]}]}))))

(rf/reg-event-fx ::imported-label-created
  (fn [_ [_ label-group-id labels label-idx context remaining-files existing-names status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [::create-imported-labels label-group-id labels label-idx context remaining-files existing-names]]}))

(rf/reg-event-fx ::import-failed
  (fn [{:keys [db]} _]
    {:db         (-> db
                     (dissoc ::import-in-progress?)
                     (dissoc ::import-conflict-state))
     :dispatch-n [[::clear-import-upload-state]
                  [:notifications/add :toast :error :error/error :labels/import-failed]]}))

(rf/reg-event-fx ::initialize-zip-upload
  (fn [_ [_ context file md5-hash]]
    (let [content-type (or (.-type file) "application/zip")
          filename     (.-name file)]
      {:dispatch [:comm/command-monitored
                  {:command    :attachment/initialize-upload
                   :data       {:content-type content-type
                                :md5-digest   md5-hash}
                   :on-success [::start-zip-upload context file md5-hash filename content-type]
                   :on-done    [::import-failed]
                   :on-error   [::import-failed]}]})))

(rf/reg-fx ::http-put
  (fn [{:keys [url headers body on-success on-failure]}]
    (PUT url
      {:headers       headers
       :body          body
       :handler       (fn [_] (>evt on-success))
       :error-handler (fn [_] (>evt on-failure))})))

(rf/reg-event-fx ::start-zip-upload
  (fn [_ [_ context file md5-hash filename content-type _status {:keys [file-id upload-url]}]]
    {::http-put {:url        upload-url
                 :headers    {"Content-Type" content-type}
                 :body       file
                 :on-success [::zip-upload-complete context file-id filename content-type md5-hash]
                 :on-failure [::import-failed]}}))

(rf/reg-event-fx ::zip-upload-complete
  (fn [_ [_ context file-id filename content-type md5-hash]]
    {:dispatch [:comm/command-monitored
                {:command    :label/parse-labels-file
                 :method     :post
                 :data       {(:parent-id-key context) (:parent-id-value context)
                              :file-id                 file-id
                              :file/filename           filename
                              :file/content-type       content-type
                              :file/md5                md5-hash}
                 :on-success [::zip-import-success context]
                 :on-done    [::zip-import-failure]
                 :on-error   [::import-failed]}]}))

(defn- parse-txt-content
  "Parse CSV/TXT content.
   Supports label names separated by newlines and/or commas.
   Returns vector of trimmed, non-blank label names."
  [content]
  (->> (str/split-lines content)
       (mapcat #(str/split % #","))
       (map str/trim)
       (remove str/blank?)
       vec))

(rf/reg-event-fx ::zip-import-success
  (fn [_ [_ context _status {:keys [parsed-files existing-names]}]]
    (let [normalized-files (mapv #(update % :labels (fn [labels]
                                                      (->> labels
                                                           (mapcat parse-txt-content)
                                                           vec)))
                                 parsed-files)]
      {:dispatch-n [[::clear-import-upload-state]
                    [::process-import-files context normalized-files existing-names]]})))

(rf/reg-event-fx ::zip-import-failure
  (fn [{:keys [db]} [_ _status response]]
    {:db         (dissoc db ::import-in-progress?)
     :dispatch-n [[::clear-import-upload-state]
                  [:notifications/add :toast :error :error/error (or (:error response) :labels/import-failed)]]}))

(defn- parse-single-txt
  "Parse a single txt file into a label group map.
   Filename (without extension) becomes the group name.
   Returns {:name \"GroupName\" :labels [\"label1\" \"label2\"]}."
  [filename content]
  (let [group-name (-> filename
                       (str/replace #"(?i)\.(csv|txt)$" "")
                       str/trim)
        labels     (parse-txt-content content)]
    {:name   group-name
     :labels labels}))

(defn- valid-label-group?
  "Validate a single label group map."
  [item]
  (and (map? item)
       (string? (:name item))
       (not (str/blank? (:name item)))
       (vector? (:labels item))
       (every? #(and (string? %) (not (str/blank? %))) (:labels item))))

(defn valid-import-data?
  "Validate that imported data is a vector of valid label group maps."
  [data]
  (and (vector? data)
       (every? valid-label-group? data)))

(defn- is-csv-or-txt-file?
  "Check if the filename indicates a CSV or TXT file."
  [filename]
  (let [lower (str/lower-case filename)]
    (or (str/ends-with? lower ".csv")
        (str/ends-with? lower ".txt"))))

(defn- is-zip-file?
  "Check if the filename indicates a ZIP file."
  [filename]
  (str/ends-with? (str/lower-case filename) ".zip"))

(defn file-size-valid?
  "Check if a file size is within the allowed limit for import."
  [size]
  (<= size label-model/max-import-file-size))

(defn export-modal
  "Modal for selecting label groups to export as CSV (single) or ZIP (multiple).
   context is a map with :download-command, :parent-id-key, :parent-id-value."
  [{:keys [tr label-groups context]}]
  (let [open?        (<sub [::export-modal-open?])
        selected-ids (<sub [::selected-group-ids-to-export])
        exporting?   (<sub [::export-in-progress?])
        close-fn     #(>evt [::close-export-modal])]
    [ui-modal/composed-input-modal
     {:on-close close-fn
      :open     open?}
     (cc/modal-header {:close-modal close-fn
                       :title       (tr [:labels/select-groups-to-export])})
     (cc/modal-body {}
                    [:div
                     [:div.flex.justify-between.items-center.pt-3
                      [:div.flex.gap-2
                       (cc/button {:kind     :ghost
                                   :size     :small
                                   :disabled exporting?
                                   :on-click #(>evt [::select-all-export-groups (mapv :db/id label-groups)])}
                                  (tr [:multi-select/select-all]))
                       (cc/button {:kind     :ghost
                                   :size     :small
                                   :disabled exporting?
                                   :on-click #(>evt [::deselect-all-export-groups])}
                                  (tr [:reporting/deselect-all]))]]
                     [:div.border.rounded.p-4.max-h-96.overflow-y-auto
                      (if (seq label-groups)
                        (for-frag [group label-groups]
                          (let [group-id    (:db/id group)
                                checked?    (contains? selected-ids group-id)
                                label-count (count (label-model/active-labels group))]
                            ^{:key group-id}
                            [cc/checkbox {:id         (str "export-group-" group-id)
                                          :label-text (str (label-model/label-group-name group)
                                                           " (" label-count " "
                                                           (str/lower-case (tr [:labels/labels])) ")")
                                          :checked    checked?
                                          :on-change  #(>evt [::toggle-export-group-selection group-id])}]))
                        [:div.text-center.text-gray-500.py-4
                         (tr [:labels/no-groups])])]])
     (cc/modal-footer {}
                      (cc/button {:kind     :secondary
                                  :disabled exporting?
                                  :on-click close-fn}
                                 (tr [:cancel]))
                      (cc/button {:kind     :primary
                                  :icon     (when exporting? i/refresh)
                                  :disabled (or exporting? (empty? selected-ids))
                                  :on-click #(>evt [::export-labels context selected-ids])}
                                 (tr [:labels/export-labels])))]))

(defn import-conflict-modal
  "Modal for handling name conflicts during import.
   context is a map with :create-group-command, :parent-id-key, :parent-id-value."
  [{:keys [tr context]}]
  (let [conflict-state (<sub [::import-conflict-state])
        open?          (boolean conflict-state)
        close-fn       #(>evt [::close-import-conflict-modal])]
    (when open?
      (let [{:keys [original-name new-name existing-names]} conflict-state
            name-valid?                                     (label-model/conflict-new-name-valid? new-name existing-names)]
        [ui-modal/composed-input-modal
         {:on-close close-fn
          :open     open?}
         (cc/modal-header {:close-modal close-fn
                           :title       (tr [:labels/name-conflict-title])})
         (cc/modal-body {}
                        [:div.space-y-4
                         [:div (tr [:labels/name-conflict-description] [original-name])]
                         [cc/text-input {:id         "conflict-rename-input"
                                         :label-text (tr [:labels/new-group-name])
                                         :value      (or new-name "")
                                         :on-change  #(>evt [::update-conflict-new-name (-> % .-target .-value)])}]])
         (cc/modal-footer {}
                          (cc/button {:kind     :secondary
                                      :on-click #(>evt [::skip-conflicting-import context])}
                                     (tr [:labels/skip-import]))
                          (cc/button {:kind     :primary
                                      :disabled (not name-valid?)
                                      :on-click #(>evt [::import-with-renamed-group context])}
                                     (tr [:labels/import-with-new-name])))]))))

(defn- handle-import-file
  "Process import file. Supports CSV, TXT, and ZIP files.
   CSV/TXT: Filename (without extension) becomes the group name, parsed locally.
   ZIP: Uploaded to backend for secure extraction via laundry.
   context is `inspection-context` or `workspace-context`, with :parent-id-value filled in."
  [context existing-names file file-hash]
  (let [filename (.-name file)
        on-error #(do
                    (>evt [::clear-import-upload-state])
                    (>evt [:notifications/add :toast :error :error/error :labels/import-invalid-file]))]
    (if-not (file-size-valid? (.-size file))
      (do
        (>evt [::clear-import-upload-state])
        (>evt [:notifications/add :toast :error :error/error :labels/import-file-too-large]))
      (cond
        (is-zip-file? filename)
        (>evt [::initialize-zip-upload context file file-hash])

        (is-csv-or-txt-file? filename)
        (let [reader (js/FileReader.)]
          (set! (.-onload reader)
                #(let [content (-> % .-target .-result)
                       parsed  [(parse-single-txt filename content)]]
                   (if (valid-import-data? parsed)
                     (>evt [::process-import-files context parsed existing-names])
                     (on-error))))
          (set! (.-onerror reader) on-error)
          (set! (.-onabort reader) on-error)
          (.readAsText reader file))

        :else
        (on-error)))))

(defn import-button
  "Import button using shared file-picker-button component.
   Accepts CSV/TXT/ZIP files and reuses the existing import processing flow."
  [{:keys [tr context existing-names disabled? class-name kind size icon]
    :or   {kind :tertiary size :default}}]
  [upload/file-picker-button
   {:id           (str "label-import-file-picker-" (:parent-id-value context))
    :tr           tr
    :accept-types [".csv" ".txt" ".zip"]
    :button-opts  (p/assoc-if {:kind             kind
                               :size             size
                               :disabled         disabled?
                               :icon-description (tr [:labels/import-labels])}
                              :class-name class-name
                              :icon       icon)
    :on-select-fn (fn [{:keys [file component-id file-hash]}]
                    (>evt [::set-import-upload-state component-id file-hash])
                    (handle-import-file context existing-names file file-hash))}
   (tr [:labels/import-labels])])
